import type {EnvironmentSubmit} from '../use-environment-selection'
import type {EnvironmentOption} from './environment-option'
import type {
  AhpProvisioningTarget,
  AhpSpaceSelection,
  DirectTurnTelemetrySeed,
  PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import type {McpPermissionMode} from '@github-ui/agent-sessions-types/mcp-permission-mode'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import type {CopilotCustomInstructions} from '@github-ui/copilot-chat-types'
import type {SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'

interface DispatchEnvironmentSubmissionInput {
  selectedEnvironment: EnvironmentOption | null
  onEnvironmentSubmit?: EnvironmentSubmit
  prompt: PreparedPrompt
  customAgent?: {slug: string; displayName?: string}
  repositoryInstructions?: CopilotCustomInstructions[]
  agentMode?: AgentMode
  approvalMode?: SessionApprovalMode
  repositoryNwo?: string
  baseRef?: string
  chatLaunch?: boolean
  mcpPermissionMode?: McpPermissionMode
  failureOwner?: 'caller'
  provisioningTarget?: AhpProvisioningTarget
  scopeLogin?: string
  space?: {owner: string; slug: string}
  initialSelectedSpace?: AhpSpaceSelection
  directTurnTelemetrySeed?: DirectTurnTelemetrySeed
}

export type EnvironmentSubmissionDispatchResult =
  | {dispatched: false}
  | {dispatched: true; outcome: 'submitted'; ownsPromptDraft: boolean}
  | {dispatched: true; outcome: 'failure-consumed'}

export async function dispatchEnvironmentSubmissionWithResult({
  selectedEnvironment,
  onEnvironmentSubmit,
  prompt,
  customAgent,
  repositoryInstructions,
  agentMode,
  approvalMode,
  repositoryNwo,
  baseRef,
  chatLaunch,
  mcpPermissionMode,
  failureOwner,
  provisioningTarget,
  scopeLogin,
  space,
  initialSelectedSpace,
  directTurnTelemetrySeed,
}: DispatchEnvironmentSubmissionInput): Promise<EnvironmentSubmissionDispatchResult> {
  const isRelayBacked = selectedEnvironment?.kind === 'ahp' || selectedEnvironment?.kind === 'sandbox'
  if (isRelayBacked && selectedEnvironment.environmentId && onEnvironmentSubmit) {
    const result = await onEnvironmentSubmit({
      environmentId: selectedEnvironment.environmentId,
      prompt,
      ...(customAgent ? {customAgent} : {}),
      ...(repositoryInstructions?.length ? {repositoryInstructions} : {}),
      ...(agentMode ? {agentMode} : {}),
      ...(approvalMode ? {approvalMode} : {}),
      ...(repositoryNwo ? {repositoryNwo} : {}),
      ...(baseRef ? {baseRef} : {}),
      ...(selectedEnvironment.kind === 'sandbox' ? {managedSandbox: true} : {}),
      ...(chatLaunch ? {chatLaunch} : {}),
      ...(mcpPermissionMode ? {mcpPermissionMode} : {}),
      ...(failureOwner ? {failureOwner} : {}),
      ...(provisioningTarget ? {provisioningTarget} : {}),
      ...(scopeLogin ? {scopeLogin} : {}),
      ...(selectedEnvironment.kind === 'sandbox' && space ? {space} : {}),
      ...(selectedEnvironment.kind === 'sandbox' && initialSelectedSpace ? {initialSelectedSpace} : {}),
      ...(directTurnTelemetrySeed ? {directTurnTelemetrySeed} : {}),
    })
    if (result?.status === 'failure-consumed') {
      return {dispatched: true, outcome: 'failure-consumed'}
    }
    return {
      dispatched: true,
      outcome: 'submitted',
      ownsPromptDraft: result?.status === 'submitted' ? result.ownsPromptDraft : true,
    }
  }
  return {dispatched: false}
}

export async function dispatchEnvironmentSubmission(input: DispatchEnvironmentSubmissionInput): Promise<boolean> {
  return (await dispatchEnvironmentSubmissionWithResult(input)).dispatched
}
